import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import api from '../config/api';
import { toast } from '../hooks/use-toast';

/* /guest-access — people outside Acuity Professional ask to use AcuityFlo.
   The admins are told; the email can sign in (with the password chosen here)
   only after an admin approves it in Settings → Members. */
const GuestAccessPage = () => {
  const prefill = useLocation().state || {};
  const [form, setForm] = useState({
    name: prefill.name || '', email: prefill.email || '', company: '', reason: '', password: '', confirm: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast({ title: 'Passwords don’t match', description: 'Type the same password in both boxes.', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const { confirm, ...body } = form;
      await api.post('/auth/guest-request', body);
      setSent(true);
    } catch (err) {
      toast({ title: 'Request not sent', description: err.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50 px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center">
            <img src="/acuity-logo.png" alt="Acuity Professional" className="h-12 object-contain" />
          </div>
          <p className="-mt-2 text-center text-xs font-medium uppercase tracking-wider text-gray-400">For internal use only</p>
          {!sent && (
            <>
              <CardTitle className="text-2xl text-center">Request guest access</CardTitle>
              <CardDescription className="text-center">
                For people outside Acuity Professional. An admin reviews every request — you can sign in once it’s approved.
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4 text-center" data-testid="guest-request-sent">
              <CheckCircle2 className="mx-auto h-10 w-10 text-green-500" />
              <p className="text-lg font-semibold text-gray-900">Request sent</p>
              <p className="text-sm text-gray-600">
                An Acuity Professional admin will review it. When it’s approved you’ll get an email, and you can sign in
                with <span className="font-medium">{form.email.trim().toLowerCase()}</span> and the password you chose.
              </p>
              <Link to="/login" className="inline-block text-sm font-medium text-orange-600 hover:text-orange-700">Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="guest-request-form">
              <div className="space-y-2">
                <Label htmlFor="guest-name">Full name</Label>
                <Input id="guest-name" value={form.name} onChange={set('name')} required autoComplete="name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest-email">Email</Label>
                <Input id="guest-email" type="email" value={form.email} onChange={set('email')} required autoComplete="email" placeholder="you@company.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest-company">Company <span className="font-normal text-gray-400">(optional)</span></Label>
                <Input id="guest-company" value={form.company} onChange={set('company')} autoComplete="organization" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest-reason">Why do you need access? <span className="font-normal text-gray-400">(optional)</span></Label>
                <textarea id="guest-reason" value={form.reason} onChange={set('reason')} rows={3} maxLength={1000}
                  placeholder="e.g. Working with Acuity on a shared client board"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest-password">Choose a password</Label>
                <Input id="guest-password" type="password" value={form.password} onChange={set('password')} required autoComplete="new-password" />
                <p className="text-xs text-gray-500">At least 12 characters, with upper and lower case letters, a number and a symbol.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest-confirm">Confirm password</Label>
                <Input id="guest-confirm" type="password" value={form.confirm} onChange={set('confirm')} required autoComplete="new-password" />
              </div>
              <Button type="submit" disabled={sending}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700">
                {sending ? 'Sending…' : 'Send request'}
              </Button>
              <p className="text-center text-sm">
                <Link to="/login" className="font-medium text-orange-600 hover:text-orange-700">Back to sign in</Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GuestAccessPage;
